import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'
import { useLazyGetBbgnPrintPreviewQuery } from '@/store/api/saleAllocationBbgnPrintApi'

// Access port: rpCtrlSalesBBGN ("Biên bản giao nhận" / goods handover record).
// frmCtrlSalesMainSubFormSaleAlc btnPrintBBGN_Click → set query
// CtrlSalesQryBBGNPrint (public_v_sale_allocation_print_scs WHERE delivery_order
// + sale_contract) → DoCmd.OpenReport "rpCtrlSalesBBGN". Self-fetching dialog:
// lazy-loads the HTML preview from the (TODO) BE endpoint, mirrors DoPrintDialog
// (iframe srcDoc preview + Print → printAndUploadReport). canEdit gates Print.

interface SalesAllocationBbgnPrintDialogProps {
  open: boolean
  /**
   * Selected allocation row `delivery_order`. Access guard: empty/null → warn
   * "Delivery order must be not null!" and never opens the report.
   */
  deliveryOrder: string
  /** Selected allocation row `sale_contract` (second WHERE key). */
  saleContract: string
  /** Print button enabled only with write access (4-layer permission rule). */
  canEdit?: boolean
  onClose: () => void
}

// Override CSS preview để fit A4 trong iframe scaled (memory: print fit A4).
// Same set as DoPrintDialog (access-report + .ctl absolute positioning).
const PRINT_PREVIEW_CSS = `
@page { size: A4 !important; margin: 0 !important; }
@media print {
  @page { size: A4 !important; margin: 0 !important; }
  body { margin: 0 !important; }
  .report-page { width: 210mm !important; max-width: none !important; min-height: 297mm !important; padding: 9mm 11mm !important; }
}
@media screen and (max-width: 600px) {
  .report-page { min-height: auto !important; max-width: 100% !important; width: 100% !important; padding: 4mm !important; margin: 0 !important; }
  body { margin: 0 !important; background: #fff !important; }
}
body { margin: 0 !important; }
.print-action { display: none !important; }
.report-page { width: 100% !important; max-width: 200mm !important; padding: 0 !important; min-height: auto !important; margin: 0 auto !important; }
.access-report { position: relative !important; margin: 0 auto !important; background: #fff !important; }
.ctl { position: absolute !important; overflow: visible !important; line-height: 1.2 !important; }
.ctl.bold { font-weight: 700 !important; }
.ctl.align-center { text-align: center !important; }
.ctl.align-right { text-align: right !important; }
.ctl img { width: 100% !important; height: 100% !important; object-fit: contain !important; object-position: left center !important; display: block !important; }
`.trim()

/** Inject base href + PRINT_PREVIEW_CSS override + strip inline print button. */
function buildBbgnPreviewHtml(rawHtml: string): string {
  const withoutInlinePrint = rawHtml.replace(/<button\s+class="print-action[^>]*>[\s\S]*?<\/button>/g, '')
  const withBase = withoutInlinePrint.replace('<head>', `<head><base href="${window.location.origin}/">`)
  const styleTag = `<style id="bbgn-print-preview-overrides">${PRINT_PREVIEW_CSS}</style>`
  if (withBase.includes('</head>')) return withBase.replace('</head>', `${styleTag}</head>`)
  return `${styleTag}${withBase}`
}

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

export default function SalesAllocationBbgnPrintDialog({
  open,
  deliveryOrder,
  saleContract,
  canEdit = true,
  onClose,
}: SalesAllocationBbgnPrintDialogProps) {
  const [toastMessage, setToastMessage] = useState('')
  const [fetchPreview, { data, isFetching, isError, error }] = useLazyGetBbgnPrintPreviewQuery()

  const iframeTitle = 'BBGN Print Preview'
  const hasDeliveryOrder = Boolean(deliveryOrder?.trim())

  // Access guard + lazy fetch: only request the report once the dialog is opened
  // for a row that actually has a delivery_order.
  useEffect(() => {
    if (!open) return
    if (!hasDeliveryOrder) {
      setToastMessage('Delivery order must be not null!')
      onClose()
      return
    }
    void fetchPreview({ deliveryOrder: deliveryOrder.trim(), saleContract: saleContract?.trim() ?? '' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasDeliveryOrder, deliveryOrder, saleContract])

  // Surface a fetch error (English only — never reveal Access/report names).
  useEffect(() => {
    if (open && isError && error) {
      setToastMessage('Unable to load the handover record preview.')
    }
  }, [open, isError, error])

  const html = data?.html ?? ''
  const previewHtml = html ? buildBbgnPreviewHtml(html) : ''
  const idPart = data?.title || deliveryOrder
  const title = idPart ? `Print Preview - BBGN (${idPart})` : 'Print Preview - BBGN'
  const loading = open && hasDeliveryOrder && isFetching

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog
        open={open && Boolean(html)}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box
            component="span"
            title={title}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            BBGN
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} size="small">
              Cancel
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                size="small"
                disabled={!html}
                onClick={() => {
                  const printTitle = `${sanitizePdfFileNamePart(idPart || 'handover_record')}_Ctrl_Sales_BBGN.pdf`
                  void printAndUploadReport({
                    iframeTitle,
                    reportName: 'rpCtrlSalesBBGN',
                    printTitle,
                    html: previewHtml,
                    onUploadError: setToastMessage,
                  })
                }}
              >
                Print
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  )
}
